/**
 * Paragraph15aEditDialog — Modal zum Ändern des Privat-Anteils einer Anlage
 *
 * Zeigt eine Live-Vorschau der §15a UStG-Vorsteuerberichtigung, die durch
 * die Änderung ausgelöst wird, und triggert beim Bestätigen den Backend-PATCH.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

const CORRECTION_PERIOD_YEARS = 5;

export default function Paragraph15aEditDialog({ open, onClose, asset, onUpdated }) {
  const [newPct, setNewPct] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && asset) {
      setNewPct(asset.private_use_percentage || 0);
    }
  }, [open, asset]);

  if (!asset) return null;

  const oldPct = asset.private_use_percentage || 0;
  const diff = newPct - oldPct;
  const vat = asset.vat_amount || 0;
  // Diff > 0: mehr privat → weniger betrieblich → Berichtigung NACH UNTEN
  // 1/5 Jahres-Anteil, signiert: negative wenn private steigt
  const acqYear = parseInt((asset.acquisition_date || "2026")[0]) > 0
    ? parseInt(asset.acquisition_date.slice(0, 4))
    : new Date().getFullYear();
  const currentYear = new Date().getFullYear();
  const yearsInPeriod = currentYear - acqYear;
  const inPeriod = yearsInPeriod >= 0 && yearsInPeriod < CORRECTION_PERIOD_YEARS;
  // newBusiness - oldBusiness = -diff (more priv = less business)
  const annualCorrection = inPeriod ? (vat * (-diff / 100) / CORRECTION_PERIOD_YEARS) : 0;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await api.patch(`/fixed-assets/${asset.id}`, {
        private_use_percentage: newPct,
      });
      toast.success(
        Math.abs(annualCorrection) > 0.005
          ? `Privat-Anteil aktualisiert · § 15a-Korrektur ${annualCorrection >= 0 ? "+" : ""}${annualCorrection.toFixed(2)} € verbucht`
          : "Privat-Anteil aktualisiert"
      );
      onUpdated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="paragraph-15a-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Privat-Anteil ändern
          </DialogTitle>
          <DialogDescription>
            {asset.name} · Vorsteuer original {vat.toFixed(2)} €
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Slider mit neuem Wert */}
          <div>
            <Label className="flex items-center justify-between">
              <span>Neuer Privat-Anteil</span>
              <span className="text-2xl font-bold tabular-nums text-emerald-700">{newPct}%</span>
            </Label>
            <Slider
              value={[newPct]}
              onValueChange={(v) => setNewPct(v[0])}
              min={0}
              max={99}
              step={5}
              className="mt-3"
              data-testid="new-private-slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>0% (voll betrieblich)</span>
              <span>99% (fast privat)</span>
            </div>
          </div>

          {/* Vorher/Nachher */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-around text-sm">
            <div className="text-center">
              <div className="text-xs text-slate-500">Vorher</div>
              <div className="text-lg font-bold tabular-nums">{oldPct}%</div>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <div className="text-center">
              <div className="text-xs text-slate-500">Neu</div>
              <div className="text-lg font-bold tabular-nums text-emerald-700">{newPct}%</div>
            </div>
          </div>

          {/* §15a-Vorschau */}
          {Math.abs(diff) > 0.5 && inPeriod && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="15a-preview">
              <div className="font-medium text-amber-900 flex items-center gap-1.5 mb-1">
                <AlertCircle className="h-4 w-4" />
                § 15a UStG-Vorsteuerberichtigung
              </div>
              <div className="text-amber-800 text-xs space-y-1">
                <div>
                  Anschaffungsjahr <strong>{acqYear}</strong> · aktuell im <strong>{yearsInPeriod + 1}. Jahr</strong> des
                  5-Jahres-Berichtigungs-Zeitraums.
                </div>
                <div>
                  Vorsteuer-Diff = {vat.toFixed(2)} € × {(-diff).toFixed(0)}% ÷ 5 Jahre =
                  <strong className={`ml-1 tabular-nums ${annualCorrection >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {annualCorrection >= 0 ? "+" : ""}{annualCorrection.toFixed(2)} €
                  </strong>
                </div>
                <div className="text-[11px] text-amber-700 pt-1 border-t border-amber-200">
                  {annualCorrection > 0
                    ? "Du darfst nachträglich Vorsteuer ziehen — wird in die nächste UStVA aufgenommen."
                    : "Berichtigung an das Finanzamt — wird in die nächste UStVA aufgenommen."}
                </div>
              </div>
            </div>
          )}
          {Math.abs(diff) > 0.5 && !inPeriod && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
              5-Jahres-Berichtigungszeitraum bereits abgelaufen — keine § 15a-Korrektur erforderlich.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || Math.abs(diff) < 0.5}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="confirm-15a-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Änderung bestätigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
